import React, { useCallback } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import {
  AuthTab,
  MagicLinkPanelShared,
  SignInFormShellShared,
  useSignInTabPreference,
} from '@liscio/common';
import { Button, Typography, ErrorDialog } from '@liscio/ui';

import { formSchema } from './formValidationSchema';
import { magicLinkFormSchema } from './MagicLinkPanel/validationSchema';
import { OAuthButtonRow } from './OAuthButtonRow/OAuthButtonRow';
import { PasswordPanel } from './PasswordPanel/PasswordPanel';
import {
  LOGIN_EVENTS,
  LOGIN_FROM_TABS,
  useTrackLoginEvent,
} from 'analytics/login/useTrackLoginEvent';
import { useForgotPassword } from 'fetch-utils/users/auth-hooks';

export interface SignInFormProps {
  isLoading?: boolean;
  onSubmit: (formData: LoginFormInterface) => void;
  onSendMagicLink: (email: string) => void;
  onSignInWithPasskey?: () => void;
  isPasskeySupported?: boolean;
}

export interface LoginFormInterface {
  email: string;
  password: string;
  enableRememberMe?: boolean;
  enableBiometrics?: boolean;
}

export const SignInForm = ({
  isLoading = false,
  onSubmit,
  onSendMagicLink,
  onSignInWithPasskey = () => {},
  isPasskeySupported = false,
}: SignInFormProps) => {
  const trackLoginEvent = useTrackLoginEvent();

  /**
   * LoginForm context
   */
  const {
    control,
    register,
    getValues,
    reset,
    clearErrors,
    formState: { errors },
    handleSubmit,
  } = useForm<LoginFormInterface>({
    resolver: yupResolver(formSchema),
  });
  const { activeTab, setActiveTab } = useSignInTabPreference('password');

  const { setForgotPasswordEmail } = useForgotPassword();

  // Update/reset email field when remember me loads
  const updateRememberMeEmailValue = useCallback(
    (email: string) => reset({ email }),
    [reset]
  );

  // Forgot Password Capture
  const captureForgotPasswordEmail = useCallback(() => {
    trackLoginEvent(LOGIN_EVENTS.FORGOT_PASSWORD_CLICKED, {
      from_tab:
        activeTab === 'magicLink'
          ? LOGIN_FROM_TABS.MAGIC_LINK
          : LOGIN_FROM_TABS.PASSWORD,
    });
    const email = getValues('email');
    setForgotPasswordEmail(email);
  }, [activeTab, getValues, setForgotPasswordEmail, trackLoginEvent]);

  const onTabChange = useCallback(
    (tab: AuthTab) => {
      setActiveTab(tab);
      clearErrors();
      trackLoginEvent(
        tab === 'magicLink'
          ? LOGIN_EVENTS.MAGIC_LINK_TAB_CLICKED
          : LOGIN_EVENTS.PASSWORD_TAB_CLICKED
      );
    },
    [clearErrors, setActiveTab, trackLoginEvent]
  );

  return (
    <SignInFormShellShared
      activeTab={activeTab}
      onTabChange={onTabChange}
      headerContent={
        <Typography variant="h2" textAlign="center" mb={4}>
          Sign In
        </Typography>
      }
      stackLeadingContent={<OAuthButtonRow />}
      magicLinkPanel={
        <MagicLinkPanelShared
          isLoading={isLoading}
          onSubmit={onSendMagicLink}
          validationSchema={magicLinkFormSchema}
        />
      }
      passwordPanel={
        <form onSubmit={handleSubmit(onSubmit)}>
          <PasswordPanel
            isLoading={isLoading}
            register={register}
            errors={errors}
            control={control}
            updateRememberMeEmailValue={updateRememberMeEmailValue}
            captureForgotPasswordEmail={captureForgotPasswordEmail}
          />
        </form>
      }
      stackTrailingContent={
        isPasskeySupported ? (
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={onSignInWithPasskey}
          >
            Use passkey
          </Button>
        ) : null
      }
      footerContent={
        <ErrorDialog title="Sign In Error" error={errors.root?.serverError} />
      }
    />
  );
};

export default SignInForm;
